// Utils
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';
import StepWizard from 'react-step-wizard';
import { arrayWithNoDuplicates } from '../../utils/utils';

// Styles
import '../../../src/style/index.scss';
import 'react-datepicker/dist/react-datepicker.css';

// Components
import ActionListItem from '../ActionListItem/ActionListItem';
import AsyncSelect from '../AsyncSelect';
import Button from '../Buttons/Button';
import MainSelect from '../MainSelect/MainSelect';
import GenderSelect from '../GenderSelect/GenderSelect';
import Property from '../Property/Property';
import SubjectList from '../SubjectList/SubjectList';
import TextInput from '../TextInput';
import ContactInformation from '../ContactInformation/ContactInformation';
import AddressForm from '../AddressForm/AddressForm';
import IconHolder from '../IconHolder/IconHolder';
import CheckboxDatabaseEmail from '../CheckboxDatabaseEmail/CheckboxDatabaseEmail';
import AssociatePerson from '../AssociatePerson/AssociatePerson';
import AssociatePersonSuccess from '../AssociatePersonSuccess/AssociatePersonSuccess';
import TagSelect from '../TagSelect/TagSelect';
import SearchOrCreateButton from '../SeachOrCreateButton/SearchOrCreateButton';

// Icons & Images
import { ReactComponent as DefaultPersonFrame } from '@/assets/icons/default-person-frame.svg';
import { ReactComponent as Diagram } from '@/assets/icons/diagram.svg';
import { ReactComponent as PersonFile } from '@/assets/icons/person-file.svg';
import { ReactComponent as Smiley } from '@/assets/icons/smiley-face.svg';
import { ReactComponent as PhoneBook } from '@/assets/icons/phone-book.svg';
import { ReactComponent as AddIcon } from '@/assets/icons/add-circle.svg';
import { ReactComponent as Location } from '@/assets/icons/location-pin.svg';
import { faUser } from '@fortawesome/free-solid-svg-icons';

// API
import { fetchPeoplesByName } from '../../utils/api/people';
import { fetchTags } from '@/utils/api/tags';

// Contexts
import { HeaderContext } from '@/contexts/headerContext';
import { PersonInfoContext } from '@/contexts/personContext';

// Types & Interfaces
type CustomStepProps = {
  accountId: number;
  errors?: any;
  fetchCategories?: any;
  goToStep?: any;
  hashValue?: any;
  isSubmitting?: any;
  nextStep?: any;
  previousStep?: any;
  setFieldValue?: any;
  touched?: any;
  values?: any;
  newCollaborations?: any;
  submitForm?: any;
  associatedHousehold?: object;
  setIsCreateHousehold?: Function;
};
interface FormikTypes {
  tag_list: any;
  name: string;
  collaborations: any;
  new_person_first_name: string;
  new_person_last_name: string;
  person_name: string;
  person_gid: any;
  main: boolean;
  title: string;
  new_person_gender: any;
  new_person_dob: any;
  first_name: string;
  value: any;
  contact_informations: any;
  addresses: {
    info: string;
    type: string;
  }[];
}
interface Collaboration {
  collaborator_gid: string | null;
  collaborator_name: string;
  main: boolean;
  title: string;
  new_person_first_name: string;
  new_person_last_name: string;
  new_person_gender: string;
  new_person_dob: string;
}

const DeleteCollaboration = ({
  item,
  setIsOpenDelete,
  index,
}: {
  index?: any;
  item: any;
  setIsOpenDelete?: any;
}) => {
  const { t } = useTranslation('dashboard');
  const { values, setFieldValue } = useFormikContext<FormikTypes>();

  function deleteCollab(index: number) {
    if (index > -1) {
      const originalCollabs = values.collaborations;
      originalCollabs.splice(index, 1);
      setFieldValue('collaborations', originalCollabs);
    }
  }
  return (
    <div className="mx-auto">
      <h3 className="mx-auto mt-10">
        {t('subjects.householdWizard.listCollaborations.actions.unlink', {
          collaboratorName: item.collaborator_name,
        })}
      </h3>
      <div className="flex flex-row mx-auto justify-center mt-5 ">
        <button
          type="button"
          onClick={() => setIsOpenDelete(false)}
          className="rounded-lg border-orange-500 border-1 px-3.5 mr-5 py-2.5 text-sm font-semibold text-orange-500 shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ">
          {t('common.buttons.cancel')}
        </button>
        <button
          type="button"
          onClick={() => deleteCollab(index)}
          className="rounded-lg bg-orange-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ">
          {t('common.buttons.dissociate')}
        </button>
      </div>
    </div>
  );
};

const AddHouseholdWizard = ({
  values,
  hashValue,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  fetchCategories,
  accountId,
  associatedHousehold,
  setIsCreateHousehold,
}: CustomStepProps) => {
  const navigate = useNavigate();
  const { personInfo } = useContext(PersonInfoContext);

  // Protection against invalid reloads
  // FIXME: use validation schema?
  useEffect(() => {
    if (personInfo) {
      if (values?.name === '' && hashValue >= 2) {
        navigate('/contacts/households/new/#step2');
      }
    } else {
      if (values?.name === '' && hashValue >= 2) {
        navigate('/contacts/households/new/#step1');
      }
    }
  }, []);

  useEffect(() => {
    if (window) {
      window.scrollTo(0, 0);
    }
  }, [hashValue]);

  var newCollaborations: Collaboration[] = [];
  return (
    <>
      {personInfo ? (
        <>
          <StepWizard isLazyMount={true} initialStep={hashValue} className="pb-6 p-6 h-full">
            <AssociatePerson
              accountId={accountId}
              errors={errors}
              fetchCategories={fetchCategories}
              hashValue={hashValue}
              isSubmitting={isSubmitting}
              setFieldValue={setFieldValue}
              touched={touched}
              isHousehold={true}
              setIsCreateHousehold={setIsCreateHousehold}
            />
            <AssociatePersonSuccess
              hashValue={hashValue}
              personInfo={personInfo}
              isHousehold={true}
              associatedHousehold={associatedHousehold}
            />
          </StepWizard>
        </>
      ) : (
        <StepWizard isLazyMount={true} initialStep={hashValue} className="pb-6 p-6 h-full">
          <Step1 hashValue={hashValue} errors={errors} accountId={accountId} />
          <Step2
            accountId={accountId}
            errors={errors}
            fetchCategories={fetchCategories}
            hashValue={hashValue}
            isSubmitting={isSubmitting}
            setFieldValue={setFieldValue}
            touched={touched}
          />
          <Step3 accountId={accountId} hashValue={hashValue} errors={errors} touched={touched} />
          <Step4
            accountId={accountId}
            hashValue={hashValue}
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
            newCollaborations={newCollaborations}
          />
          <Step5
            accountId={accountId}
            errors={errors}
            hashValue={hashValue}
            setFieldValue={setFieldValue}
            touched={touched}
            newCollaborations={newCollaborations}
          />
          <Step6
            accountId={accountId}
            errors={errors}
            hashValue={hashValue}
            isSubmitting={isSubmitting}
          />
        </StepWizard>
      )}
    </>
  );
};

// Welcome slide & start wizard button
const Step1 = ({ nextStep, hashValue, goToStep }: CustomStepProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const headerContext = React.useContext(HeaderContext);

  React.useEffect(() => {
    if (hashValue == 1) {
      headerContext.dispatch({
        type: 'SET_HEADER_PROPS',
        payload: {
          hamburgerMenu: false,
          homePage: false,
          closeButton: false,
          canEdit: false,
          canGoBack: true,
        },
      });
    }
  }, []);

  function goToNextStep() {
    nextStep();
    navigate('/contacts/households/new/#step2');
  }

  // Code here will run after *every* render
  useEffect(() => {
    goToStep(hashValue);
  }, [hashValue]);

  return (
    <div className="flex items-center justify-between flex-col pt-20 h-full ">
      <div className="flex justify-center flex-col w-full h-full">
        <div className="flex flex-col gap-5 justify-center w-full">
          <div className="flex justify-center mt-auto gap-5 flex-col text-center w-full">
            <IconHolder
              svgIcon={<Smiley />}
              header={t('subjects.householdWizard.welcome.title')}
              subTitle={t('subjects.householdWizard.welcome.subtitle')}
            />
          </div>
        </div>
      </div>
      <div className="w-full translate-y-32 flex flex-col gap-3 pb-6">
        <Button
          type="button"
          onClick={goToNextStep}
          text={t('subjects.addPersonForm.buttonStart')}
          variant="primary"
          size="large"
        />
        <Button
          type="button"
          link="/contacts/households"
          text={t('common.buttons.cancel')}
          variant="secondaryOrange"
          size="large"
        />
      </div>
    </div>
  );
};

// Household form
const Step2 = ({
  hashValue,
  goToStep,
  setFieldValue,
  fetchCategories,
  errors,
  touched,
  nextStep,
  accountId,
}: CustomStepProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const [nextStepAttempted, setNextStepAttempted] = useState(false);
  const headerContext = React.useContext(HeaderContext);
  const { values } = useFormikContext<FormikTypes>();

  React.useEffect(() => {
    if (hashValue == 2) {
      headerContext.dispatch({
        type: 'SET_HEADER_PROPS',
        payload: {
          hamburgerMenu: false,
          homePage: false,
          closeButton: false,
          canEdit: false,
          canGoBack: true,
        },
      });
    }
  }, []);

  function goToNextStep() {
    setNextStepAttempted(true);
    if (!errors?.name && !errors.category_id) {
      nextStep();
      navigate('/contacts/households/new/#step3');
    }
  }
  // Code here will run after *every* render
  useEffect(() => {
    goToStep(hashValue);
  }, [hashValue]);

  return (
    <div className="flex flex-col items-center justify-between w-full h-full">
      <div className="flex flex-col gap-5  mb-5">
        <IconHolder
          svgIcon={<PersonFile />}
          header={t('subjects.householdWizard.household.title')}
          subTitle={t('subjects.householdWizard.household.subtitle')}
        />
      </div>
      <div className="w-full gap-5 flex flex-col">
        <Property
          title={t('subjects.householdWizard.household.fields.name.label')}
          hint={t('subjects.householdWizard.household.fields.name.hint')}
          haveGap
          bold
          required={true}
          content={
            <TextInput
              touched={touched?.name}
              nextStepAttempted={nextStepAttempted}
              type="text"
              name="name"
              placeholder={t('subjects.householdWizard.household.fields.name.placeholder')}
            />
          }
        />
        <Property
          title={t('subjects.householdWizard.household.fields.category_id.label')}
          haveGap
          bold
          required={true}
          content={
            <AsyncSelect
              type="select"
              name="category_id"
              placeholder={t('subjects.householdWizard.household.fields.category_id.placeholder')}
              setFieldValue={setFieldValue}
              loadOptions={fetchCategories}
              isClearable
              defaultOptions
              nextStepAttempted={nextStepAttempted}
            />
          }
        />
        <TagSelect
          fetchTags={fetchTags}
          setFieldValue={setFieldValue}
          values={values}
          accountId={accountId}
        />
        <CheckboxDatabaseEmail />
      </div>
      <div className="w-full pb-6 translate-y-28">
        <Button
          type="button"
          onClick={goToNextStep}
          text={t('common.buttons.next')}
          variant="primary"
          size="large"
        />
      </div>
    </div>
  );
};

const Step3 = ({ nextStep, hashValue, goToStep, errors, touched, accountId }: CustomStepProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { values } = useFormikContext<FormikTypes>();
  const [nextStepAttempted, setNextStepAttempted] = useState(false);
  const headerContext = React.useContext(HeaderContext);

  React.useEffect(() => {
    if (hashValue == 3) {
      headerContext.dispatch({
        type: 'SET_HEADER_PROPS',
        payload: {
          hamburgerMenu: false,
          homePage: false,
          closeButton: false,
          canEdit: false,
          canGoBack: true,
        },
      });
    }
  }, []);

  function goToNextStep() {
    setNextStepAttempted(true);
    if (!errors.contact_informations) {
      nextStep();
      navigate('/contacts/households/new/#step4');
    }
  }

  useEffect(() => {
    goToStep(hashValue);
  }, [hashValue]);

  return (
    <div className="flex flex-col h-full gap-6 justify-between w-full">
      <IconHolder
        svgIcon={<PhoneBook />}
        header={t('subjects.householdWizard.contactInformation.title')}
        subTitle={t('subjects.householdWizard.contactInformation.subtitle')}
      />
      <div className="flex flex-col w-full">
        <FieldArray name="contact_informations">
          {({ push, remove }) => (
            <>
              {values.contact_informations.length > 0 &&
                values.contact_informations.map((contact_information: any, index: number) => (
                  <ContactInformation
                    key={index}
                    remove={() => remove(index)}
                    push={() => push(index)}
                    index={index}
                    item={contact_information}
                    touched={touched?.contact_informations}
                    nextStepAttempted={nextStepAttempted}
                  />
                ))}
              <div className="flex justify-start text-orange-500 mt-2 ">
                <button
                  type="button"
                  className="my-auto flex flex-row"
                  onClick={() => push({ info: '', type: 'Email', main: false })}>
                  <div className="flex justify-center items-center font-semibold mr-2">
                    <AddIcon style={{ stroke: '#F97316' }} />
                  </div>
                  <div className="mt-0.5">{t('subjects.addPersonForm.addContact')}</div>
                </button>
              </div>
            </>
          )}
        </FieldArray>
      </div>
      <div className="w-full translate-y-32 flex flex-col gap-3 pb-6">
        <div className="w-full">
          <Button
            type="button"
            onClick={goToNextStep}
            text={t('common.buttons.next')}
            variant="primary"
            size="large"
          />
        </div>
        <Button
          type="submit"
          text={t('subjects.addPersonForm.finish')}
          variant="secondaryOrange"
          size="large"
        />
      </div>
    </div>
  );
};

const Step4 = ({ hashValue, goToStep, setFieldValue, newCollaborations }: CustomStepProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { values } = useFormikContext<FormikTypes>();
  const headerContext = React.useContext(HeaderContext);
  const { personInfo } = useContext(PersonInfoContext);

  React.useEffect(() => {
    if (hashValue == 4) {
      headerContext.dispatch({
        type: 'SET_HEADER_PROPS',
        payload: {
          hamburgerMenu: false,
          homePage: false,
          closeButton: false,
          canEdit: false,
          canGoBack: true,
        },
      });
    }
  }, []);

  function goToNextStep() {
    if (personInfo) {
      const newPerson = {
        collaborator_name: personInfo.name,
        collaborator_gid: personInfo.gid,
        main: true,
      };
      const updatedCollaborations = Array.isArray(newCollaborations)
        ? [newPerson, ...newCollaborations]
        : [newPerson];
      setFieldValue('collaborations', updatedCollaborations);
      navigate('/contacts/households/new/#step6');
    } else {
      navigate('/contacts/households/new/#step5');
    }
  }

  useEffect(() => {
    goToStep(hashValue);
  }, [hashValue]);

  return (
    <div className="flex flex-col items-center justify-between w-full h-full">
      <div className="flex flex-col gap-6 h-full mb-5">
        <IconHolder
          svgIcon={<Location />}
          header={t('subjects.householdWizard.address.title')}
          subTitle={t('subjects.householdWizard.address.subtitle')}
        />
      </div>
      <div className="flex flex-col w-full">
        <FieldArray name="addresses">
          {({ push, remove }) => (
            <>
              {values.addresses.length > 0 &&
                values.addresses.map((address, index) => (
                  <AddressForm
                    key={index} // Don't forget to add the key for dynamic lists.
                    remove={() => remove(index)}
                    index={index}
                    setFieldValue={setFieldValue}
                    personInfo={personInfo}
                  />
                ))}
              <div className="flex justify-start text-orange-500 mt-2 ">
                <button
                  type="button"
                  className="my-auto flex flex-row"
                  onClick={() => push({ info: '', type: 'Email' })}>
                  <div className="flex justify-center items-center font-semibold mr-2">
                    <AddIcon style={{ stroke: '#F97316' }} />
                  </div>
                  <div className="mt-0.5">{t('subjects.addPersonForm.addAddress')}</div>
                </button>
              </div>
            </>
          )}
        </FieldArray>
      </div>
      <div className="w-full  pb-6 translate-y-24 gap-3 flex flex-col">
        <div className="w-full">
          <Button
            type="button"
            onClick={goToNextStep}
            text={t('common.buttons.next')}
            variant="primary"
            size="large"
          />
        </div>
        <Button
          type="submit"
          text={t('subjects.addPersonForm.finish')}
          variant="secondaryOrange"
          size="large"
        />
      </div>
    </div>
  );
};

// Associate a new person (from a selection or new) to the upcoming household
// - search a person and set collaboration's attributes (main, role)
// - create a person (see PersonWWizard#step2: first_name, last_name, gender, dob) and collaboration's attributes (main, role)
const Step5 = ({
  nextStep,
  hashValue,
  goToStep,
  setFieldValue,
  accountId,
  newCollaborations,
}: CustomStepProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { values } = useFormikContext<FormikTypes>();
  const [disabledSubmit, setDisabledSubmit] = useState(true); // TODO: reactivate when search/new logic implemented
  const [addingPerson, setAddingPerson] = useState(true);
  const [searchShown, setSearchShown] = useState(false);
  const headerContext = React.useContext(HeaderContext);

  React.useEffect(() => {
    if (hashValue == 5) {
      headerContext.dispatch({
        type: 'SET_HEADER_PROPS',
        payload: {
          title: t('subjects.addHouseholdWizard.association'),
          hamburgerMenu: false,
          homePage: false,
          closeButton: false,
          canEdit: false,
          canGoBack: true,
        },
      });
    }
  }, []);

  // TODO: refactor when we can
  function goToNextStep() {
    // Set the collaborations array while taking care of `collaborator_gid` duplicates
    const currentCollaborations = values.collaborations;
    const newPersonName = addingPerson
      ? `${values.new_person_first_name} ${values.new_person_last_name}`
      : values.person_name;
    const newCollaboration = {
      collaborator_gid: addingPerson ? null : values.person_gid,
      collaborator_name: newPersonName,
      main: values.main,
      title: values.title,
      new_person_first_name: values.new_person_first_name,
      new_person_last_name: values.new_person_last_name,
      new_person_gender: values.new_person_gender,
      new_person_dob: values.new_person_dob,
    };
    newCollaborations = addingPerson
      ? currentCollaborations.concat(newCollaboration)
      : arrayWithNoDuplicates(currentCollaborations, newCollaboration, 'collaborator_gid');
    // Set field values
    setFieldValue('collaborations', newCollaborations);
    setFieldValue('person_name', newPersonName);
    // Reset new person values
    setFieldValue('new_person_first_name', '');
    setFieldValue('new_person_last_name', '');
    setFieldValue('new_person_gender', '');
    setFieldValue('new_person_dob', '');
    // Reset some collaboration values
    setFieldValue('title', '');
    setFieldValue('main', false);
    setAddingPerson(true);
    nextStep();
    navigate('/contacts/households/new/#step6');
  }

  const fetchPeople = (query: string) =>
    fetchPeoplesByName(accountId, query).then(response =>
      response.map((person: any) => ({
        value: person.gid,
        label: person.name,
      })),
    );

  const toggleMode = (toggle: boolean) => {
    setSearchShown(!toggle);
    const newPersonName = `${values.new_person_first_name} ${values.new_person_last_name}`;

    setFieldValue('person_name', newPersonName);
    setAddingPerson(true);
  };

  useEffect(() => {
    if (searchShown) {
      if (values.new_person_first_name || values.first_name) {
        setDisabledSubmit(false);
      } else {
        setDisabledSubmit(true);
      }
    }
  }, [values.new_person_first_name, values.first_name]);

  // Code here will run after *every* render
  useEffect(() => {
    goToStep(hashValue);
  }, [hashValue]);

  return (
    <div className="flex flex-col items-center justify-between h-full">
      <div className="flex flex-col gap-5 h-full mb-5">
        <IconHolder
          svgIcon={<DefaultPersonFrame />}
          header={t('subjects.householdWizard.addCollaboration.title')}
          subTitle={t('subjects.householdWizard.addCollaboration.subtitle')}
        />
      </div>
      <div className="w-full gap-5 flex flex-col">
        <SearchOrCreateButton
          toggleSearch={toggleMode}
          searchShown={searchShown}
          searchFunction={() => {
            setDisabledSubmit(true);
          }}
          createFunction={() => {
            setDisabledSubmit(true);
            if (values.new_person_first_name) {
              setDisabledSubmit(false);
            }
          }}
        />
        {!searchShown ? (
          <div className="w-full gap-5 flex flex-col">
            <Property
              haveGap
              bold
              title={t('placeHolders.search')}
              content={
                <AsyncSelect
                  type="select"
                  name="person_gid"
                  placeholder={t(
                    'subjects.householdWizard.addCollaboration.fields.person_gid.placeholder',
                  )}
                  setFieldValue={setFieldValue}
                  afterSelect={option => {
                    setFieldValue('person_name', option?.label);
                    {
                      searchShown &&
                        setFieldValue(
                          'person_name',
                          `${values.new_person_first_name} ${values.new_person_last_name}`,
                        );
                    }
                    setAddingPerson(false);
                    {
                      option?.label ? setDisabledSubmit(false) : setDisabledSubmit(true);
                    }
                  }}
                  loadOptions={fetchPeople}
                  isClearable
                  defaultOptions
                />
              }
            />
          </div>
        ) : (
          <div className="w-full gap-2 flex flex-col">
            <Property
              title={t('common.firstname')}
              bold={true}
              content={<TextInput type="text" name="new_person_first_name" />}
            />
            <Property
              title={t('common.lastname')}
              bold={true}
              content={<TextInput type="text" name="new_person_last_name" />}
            />
            <Property
              haveGap
              bold={true}
              title={t('subjects.addPersonForm.gender.genderTitle')}
              content={<GenderSelect name="new_person_gender" />}
            />
            <Property
              title={t('subjects.properties.dob')}
              haveGap
              bold={true}
              content={
                <div className="flex relative">
                  <TextInput
                    id="date"
                    type="date"
                    isDob={true}
                    name="new_person_dob"
                    containerClasses="w-full"
                  />
                </div>
              }
            />
          </div>
        )}
        <Property
          title={t('subjects.householdWizard.addCollaboration.fields.person_title.label')}
          hint={t('subjects.householdWizard.addCollaboration.fields.person_title.hint')}
          haveGap
          bold
          content={
            <TextInput
              type="text"
              name="title"
              placeholder={t(
                'subjects.householdWizard.addCollaboration.fields.person_title.placeholder',
              )}
            />
          }
        />
        <Property
          hint={t('subjects.householdWizard.addCollaboration.fields.person_main.hint')}
          haveGap
          bold
          content={<MainSelect name="main" />}
        />
      </div>
      <div className="w-full  pb-6 translate-y-36">
        <Button
          type="button"
          onClick={goToNextStep}
          text={t('subjects.householdWizard.addCollaboration.actions.next')}
          variant="primary"
          disabled={disabledSubmit}
          size="large"
        />
      </div>
    </div>
  );
};

// Final step: View list of household members with following actions:
// - add a new household member (back to previous step)
// - finalize the creation of the household and collaborations and navigate to the household page
const Step6 = ({ previousStep, hashValue, goToStep, isSubmitting }: CustomStepProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { values } = useFormikContext<FormikTypes>();
  const headerContext = React.useContext(HeaderContext);

  React.useEffect(() => {
    if (hashValue == 5) {
      headerContext.dispatch({
        type: 'SET_HEADER_PROPS',
        payload: {
          title: t('subjects.addHouseholdWizard.association'),
          hamburgerMenu: false,
          homePage: false,
          closeButton: false,
          canEdit: false,
          canGoBack: false,
        },
      });
    }
  }, []);

  function goToPrevStep() {
    previousStep();
    navigate('/contacts/households/new/#step5');
  }

  // Code here will run after *every* render
  useEffect(() => {
    goToStep(hashValue);
  }, [hashValue]);

  return (
    <div className="flex flex-col gap-5 h-full justify-between h-full pt-10">
      <div className="flex flex-col gap-5 h-full mb-5">
        <IconHolder
          svgIcon={<Diagram />}
          header={t('subjects.householdWizard.listCollaborations.title')}
          subTitle={t('subjects.householdWizard.listCollaborations.subtitle', {
            householdName: values?.name,
            collaboratorName: values?.person_name,
          })}
        />
      </div>
      <div className="mt-2 flex flex-col gap-5 h-full">
        <div className="pl-3 pr-3 h-full">
          <SubjectList>
            {values.collaborations.map(
              (
                collaboration: {
                  collaborator_gid: any;
                  collaborator_name: string;
                  main: string | boolean | undefined;
                  title: string | undefined;
                },
                index: any,
              ) => (
                <ActionListItem
                  key={collaboration.collaborator_gid || `${collaboration.collaborator_name}`}
                  item={collaboration}
                  index={index}
                  DeleteComponent={DeleteCollaboration}
                  main={collaboration.main}
                  icon={faUser}
                  editable={true}
                  mainText={collaboration.collaborator_name}
                  mutedText={collaboration.title}
                />
              ),
            )}
          </SubjectList>
        </div>
      </div>
      <div className="w-full translate-y-32 flex flex-col gap-3 pb-6">
        <div className="w-full">
          <Button
            type="button"
            onClick={goToPrevStep}
            text={t('subjects.householdWizard.listCollaborations.actions.back')}
            variant="secondaryOrange"
            size="large"
          />
        </div>
        <div className="w-full">
          <Button
            type="submit"
            text={t('subjects.householdWizard.listCollaborations.actions.next', {
              householdName: values?.name,
            })}
            variant="primary"
            size="large"
            disabled={isSubmitting}
            className={`${
              isSubmitting
                ? 'text-disabledText bg-disabledBg w-full'
                : 'text-white bg-primaryColor w-full'
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default AddHouseholdWizard;
